import React, { useMemo, useState } from 'react';
import { Group, Loader, Stack, Text, Textarea } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import SendIcon from '@material-ui/icons/Send';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import MicIcon from '@material-ui/icons/Mic';
import { RecordOptions } from '../neoverv';
import InlineVoiceRecorder from './InlineVoiceRecorder';
import InlineAudioRecorder from './InlineAudioRecorder';
import { AI_AGENT_BASE_URL, LIVE_TRANSCRIPTION_ENABLED } from '../helpers/aiAgentApi';
import AiAgentIcon from '../assets/images/module/general/AiAgentIcon.svg';

const PLACEHOLDER = 'Ihre Frage an die KI …';

// Curated pool of example prompts shown below the input, three picked at random per mount
// so the tile doesn't show the exact same suggestions every time ("ideally automatically
// generated" per the feedback) without needing an extra model call just to render the tile.
const EXAMPLE_PROMPT_POOL = [
  'Welche Mieten sind diesen Monat noch offen?',
  'Zeig mir die letzten Belege dieser Immobilie.',
  'Wann läuft der nächste Mietvertrag aus?',
  'Erstelle eine Zahlungserinnerung für offene Mieten.',
  'Wie hoch waren die Ausgaben in den letzten 12 Monaten?',
  'Welche Zählerstände wurden zuletzt erfasst?',
  'Fass mir die letzte Betriebskostenabrechnung zusammen.',
  'Wie viele Mieteinheiten stehen aktuell leer?'
];

function pickRandomExamples(count) {
  const shuffled = [...EXAMPLE_PROMPT_POOL].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

// Dashboard entry point for the AI assistant: a short prompt typed here is handed off (via
// onSend) to open the full AI chat modal in a brand-new thread and fire it immediately, so
// the user doesn't have to repeat themselves once the modal opens. "Datei anhängen" (via
// onAttachFile) does the same handoff but opens the new thread's file picker instead —
// attaching a file needs a session folder, which only exists once that thread is created, so
// it can't happen locally in the tile the way dictation (below) can. "Aufnehmen" stays local
// either way, entirely inline in the prompt bar — no popup, which is what the original
// "inconvenient overlay" feedback was about. LIVE_TRANSCRIPTION_ENABLED picks which recorder
// renders: InlineVoiceRecorder (streams straight to /ws/transcribe, filling the textarea live)
// or InlineAudioRecorder (records one clip locally, then POSTs it to /transcribe like the old
// flow did — just without the old TranscribeModal popup around it) — see aiAgentApi.js for why
// the live path is currently off (staging's proxy doesn't forward WebSocket upgrades yet).
export default function AiPromptDashboardTile({ onSend, onAttachFile, accountId, userId, propertyId, encryptedToken }) {
  const [prompt, setPrompt] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [transcribeLoader, setTranscribeLoader] = useState(false);
  const examplePrompts = useMemo(() => pickRandomExamples(6), []);

  const handleSend = () => {
    const trimmed = prompt.trim();
    if (!trimmed) {
      return;
    }
    onSend(trimmed);
    setPrompt('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  // InlineVoiceRecorder's onError: the live-transcription socket was rejected by the credit
  // gate (or errored/dropped) — see liveTranscriptionService.js. Surfaced as a toast using
  // the backend's own message text, since it already differs by case (testing-subscription
  // vs. generally exhausted — see creditGateService.js's requiresSubscriptionUpgrade).
  const handleRecorderError = (detail) => {
    setIsRecording(false);
    showNotification({
      message: detail?.error || 'Die Aufnahme konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
      color: 'red',
    });
  };

  // Old flow's transcription: unlike InlineVoiceRecorder's live captions, this only has text
  // once the whole clip has uploaded and come back — so it sends immediately on success too
  // (same "stop = I'm done" behavior as the live path), rather than making the user click
  // send a second time.
  const uploadAudio = async (file) => {
    if (!file) return;
    setTranscribeLoader(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('accountId', accountId);
    formData.append('userId', userId);
    formData.append('propertyId', propertyId);
    formData.append('encryptedToken', encryptedToken);
    try {
      const res = await fetch(`${AI_AGENT_BASE_URL}/transcribe`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) {
        showNotification({
          message: data?.error || 'Die Aufnahme konnte nicht transkribiert werden. Bitte versuchen Sie es erneut.',
          color: 'red',
        });
        return;
      }
      const trimmed = (data.text || '').trim();
      if (trimmed) onSend(trimmed);
    } catch (err) {
      console.error(err);
      showNotification({
        message: 'Die Aufnahme konnte nicht transkribiert werden. Bitte versuchen Sie es erneut.',
        color: 'red',
      });
    } finally {
      setTranscribeLoader(false);
    }
  };

  return (
    <div className="ai-dashboard-tile">
      <Stack gap={16} justify="center" style={{ height: '100%' }}>
        <Group gap={10} wrap="nowrap" align="flex-start">
          <div className="ai-dashboard-tile-icon-badge">
            <img src={AiAgentIcon} alt="" style={{ width: '15px', filter: 'brightness(0) invert(1)' }} />
          </div>
          <Stack gap={2}>
            <Text className="ai-dashboard-tile-title">Fragen Sie die hellohousing KI</Text>
            <Text className="ai-dashboard-tile-subtitle">
              Finden Sie Hilfe bei Problemen und Fragen, lassen Sie Daten auswerten und prüfen und Aufgaben direkt von der KI erledigen.
            </Text>
          </Stack>
        </Group>
        <div className="ai-dashboard-tile-input-wrap">
          <Textarea
            variant="unstyled"
            classNames={{ input: 'ai-dashboard-tile-textarea', section: 'ai-dashboard-tile-textarea-icons-section' }}
            placeholder={PLACEHOLDER}
            disabled={isRecording || transcribeLoader}
            autosize
            minRows={1}
            maxRows={3}
            value={prompt}
            onChange={(event) => setPrompt(event.currentTarget.value)}
            onKeyDown={handleKeyDown}
            rightSectionWidth={isRecording ? 120 : transcribeLoader ? 170 : 80}
            rightSection={
              isRecording ? (
                LIVE_TRANSCRIPTION_ENABLED ? (
                  <InlineVoiceRecorder
                    accountId={accountId}
                    userId={userId}
                    propertyId={propertyId}
                    encryptedToken={encryptedToken}
                    onPartialText={(text) => setPrompt(text)}
                    onFinish={(text) => {
                      // Sends straight away (same handoff handleSend uses) rather than
                      // leaving it in the input for a second click — clicking stop is
                      // already the "I'm done" signal.
                      setIsRecording(false);
                      const trimmed = (text || '').trim();
                      if (trimmed) {
                        onSend(trimmed);
                        setPrompt('');
                      } else {
                        setPrompt('');
                      }
                    }}
                    onCancel={() => setIsRecording(false)}
                    onError={handleRecorderError}
                  />
                ) : (
                  <InlineAudioRecorder
                    onFinish={(file) => { setIsRecording(false); uploadAudio(file); }}
                    onCancel={() => setIsRecording(false)}
                  />
                )
              ) : transcribeLoader ? (
                <Group wrap="nowrap" gap={5} align="flex-end" justify="center">
                  <Text className="ai-inline-voice-recorder-timer">Wird transkribiert</Text>
                  <Loader style={{paddingTop: '2px'}} size={'xs'} type='dots' color='#5555FF'/>
                </Group>
              ) : (
                <Group wrap="nowrap" gap={6} align="center" justify="center">
                  <RecordOptions
                    showEditButton={false}
                    showDeleteButton={false}
                    listOfItems={[
                      {
                        text: 'Datei anhängen',
                        leftImage: <AttachFileIcon className="ai-chat-modal-textarea-right-section-mic-icon" />,
                        openFunction: () => onAttachFile()
                      },
                      {
                        text: 'Aufnehmen',
                        leftImage: <MicIcon className="ai-chat-modal-textarea-right-section-mic-icon" />,
                        openFunction: () => setIsRecording(true)
                      }
                    ]}
                    style={{ marginRight: '0px' }}
                    buttonStyle={{ border: 'none', background: 'transparent' }}
                    menuStyle={{ zIndex: 999999 }}
                  />
                  <SendIcon
                    className="ai-dashboard-tile-send-btn"
                    style={{
                      cursor: prompt.trim() ? 'pointer' : 'not-allowed',
                      color: prompt.trim() ? '#5555FF' : '#9CA3AF',
                      fontSize: '18px',
                      opacity: prompt.trim() ? 1 : 0.6,
                    }}
                    onClick={() => prompt.trim() && handleSend()}
                    aria-label="Anfrage senden"
                  />
                </Group>
              )
            }
          />
        </div>
        <Group gap={6} wrap="wrap" className="ai-dashboard-tile-examples">
          {examplePrompts.map((example) => (
            <button
              key={example}
              type="button"
              className="ai-dashboard-tile-example-chip"
              onClick={() => { onSend(example); setPrompt(''); }}
            >
              {example}
            </button>
          ))}
        </Group>
      </Stack>
    </div>
  );
}
